import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Dialog as RACDialog } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useVisualViewportHeight } from "../../hooks/useVisualViewportHeight";
import type { DialogFooterConfig, DialogHeaderConfig } from "../Dialog/types";
import { DialogFooter } from "../DialogFooter";
import { DialogHeader } from "../DialogHeader";
import { ErrorBoundary } from "../ErrorBoundary";
import { ModalBackdrop } from "../ModalBackdrop";

const SWIPE_DISMISS_THRESHOLD = 0.3;
const SWIPE_VELOCITY_THRESHOLD = 0.5;

export interface TrayProps extends DialogHeaderConfig, DialogFooterConfig {
  /**
   * Tray content
   */
  children?: React.ReactNode;
  /**
   * Controls whether the tray is visible
   */
  isOpen: boolean;
  /**
   * Called when the tray should close
   */
  onClose?: () => void;
  /**
   * Whether clicking the backdrop should dismiss the tray
   * @default true
   */
  isDismissable?: boolean;
  /**
   * Whether to use a transparent overlay behind the tray
   * @default false
   */
  transparentOverlay?: boolean;
  /**
   * Maximum height for the tray content area
   * @default "80vh"
   */
  maxHeight?: string;
  /**
   * Explicit height for the tray (overrides maxHeight if set)
   * Content will scroll within this fixed height
   * @default undefined
   */
  height?: string;
  /**
   * Maximum width of the tray
   * @default "640px"
   */
  maxWidth?: string;
  /**
   * Whether to include padding around tray content
   * @default true
   */
  contentPadding?: boolean;
  /**
   * Whether to show the drag handle indicator on mobile
   * @default true
   */
  showHandle?: boolean;
  /**
   * Animation variant for the tray entrance/exit
   * @default "scale" - Slide with subtle scale effect for depth
   * "slide" - Simple slide up/down
   */
  animationVariant?: "slide" | "scale";
  /**
   * Additional CSS classes for the tray container
   */
  className?: string;
}

/**
 * Tray
 *
 * Bottom anchored overlay optimized for mobile experiences.
 * Provides optional header/footer slots that align with Dialog & Drawer APIs.
 */
export function Tray({
  children,
  title,
  headerContent,
  titleAlign = "left",
  hideCloseIcon = false,
  hasBackArrow = false,
  onBack,
  isOpen,
  onClose,
  isDismissable = true,
  transparentOverlay = false,
  primaryAction,
  secondaryAction,
  footerContent,
  maxHeight = "80vh",
  height,
  maxWidth = "640px",
  contentPadding = true,
  showHandle = true,
  animationVariant = "scale",
  className,
}: TrayProps) {
  const [isHandleAnimating, setIsHandleAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Track the available visual viewport height so the tray resizes and repositions
  // when the iOS soft keyboard pushes up the viewport.
  const viewport = useVisualViewportHeight(isOpen);
  const viewportHeight = viewport?.height ?? null;
  const keyboardOffset = viewport?.keyboardOffset ?? 0;
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref for the scrollable content area — used to scroll focused inputs into view on iOS.
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStateRef = useRef<{
    isDragging: boolean;
    startY: number;
    startTime: number;
    pointerId: number | null;
  }>({
    isDragging: false,
    startY: 0,
    startTime: 0,
    pointerId: null,
  });

  // Trigger a subtle handle animation when tray opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsHandleAnimating(true);
        setTimeout(() => setIsHandleAnimating(false), 600);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset drag state when tray closes
  useEffect(() => {
    if (!isOpen) {
      setDragOffset(0);
      setIsTransitioning(false);
      dragStateRef.current = {
        isDragging: false,
        startY: 0,
        startTime: 0,
        pointerId: null,
      };
    }
  }, [isOpen]);

  // On iOS, focusing an input inside a non-body scroll container won't automatically
  // scroll that container to bring the input into view — the browser scrolls the page
  // body instead (which is locked by the modal). We listen for focusin on the scroll
  // container and manually scroll the focused element into view after the keyboard has
  // had a chance to open (small timeout lets visualViewport settle first).
  useEffect(() => {
    const scrollEl = scrollContentRef.current;
    if (!scrollEl || !isOpen) return;

    let focusScrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Only act on actual form inputs
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

      // Delay so the keyboard has time to open and visualViewport to update
      if (focusScrollTimeout) clearTimeout(focusScrollTimeout);
      focusScrollTimeout = setTimeout(() => {
        target.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 320);
    };

    scrollEl.addEventListener("focusin", handleFocusIn);
    return () => {
      scrollEl.removeEventListener("focusin", handleFocusIn);
      if (focusScrollTimeout) clearTimeout(focusScrollTimeout);
    };
  }, [isOpen]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isDismissable) return;
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;

      // Clear any pending transition timeout from previous gesture
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }

      dragStateRef.current = {
        isDragging: true,
        startY: e.clientY,
        startTime: Date.now(),
        pointerId: e.pointerId,
      };
      setIsTransitioning(false);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [isDismissable]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const { isDragging, startY, pointerId } = dragStateRef.current;
    if (!isDragging || e.pointerId !== pointerId) return;

    const deltaY = e.clientY - startY;
    setDragOffset(Math.max(0, deltaY));
  }, []);

  const handlePointerEnd = useCallback(
    (e: React.PointerEvent) => {
      const { isDragging, startY, startTime, pointerId } = dragStateRef.current;
      if (!isDragging || e.pointerId !== pointerId) return;

      const deltaY = e.clientY - startY;
      const elapsed = Date.now() - startTime;
      const velocity = deltaY / elapsed;

      dragStateRef.current = {
        isDragging: false,
        startY: 0,
        startTime: 0,
        pointerId: null,
      };

      const containerHeight = containerRef.current?.getBoundingClientRect().height ?? 0;
      const threshold = containerHeight * SWIPE_DISMISS_THRESHOLD;
      const shouldDismiss = deltaY > threshold || (velocity > SWIPE_VELOCITY_THRESHOLD && deltaY > 50);

      if (shouldDismiss && isDismissable) {
        onClose?.();
        setDragOffset(0);
      } else {
        setIsTransitioning(true);
        setDragOffset(0);
        transitionTimeoutRef.current = setTimeout(() => {
          setIsTransitioning(false);
          transitionTimeoutRef.current = null;
        }, 200);
      }
    },
    [isDismissable, onClose]
  );

  if (!isOpen) return null;

  const hasHeader = title || headerContent;
  const hasFooter = primaryAction || secondaryAction || footerContent;

  return (
    <ErrorBoundary>
      <ModalBackdrop
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose?.();
        }}
        isDismissable={isDismissable}
        transparent={transparentOverlay}
        // On iOS Safari, position:fixed overlays are anchored to the layout viewport
        // and don't shift when the soft keyboard opens. We apply bottom padding equal
        // to the keyboard height so the tray — which is flex-end aligned — gets pushed
        // up above the keyboard. Transition matches the iOS keyboard animation curve.
        style={{
          paddingBottom: keyboardOffset > 0 ? `${keyboardOffset}px` : undefined,
          transition: "padding-bottom 0.25s ease-out",
        }}
        className="flex items-end justify-center p-0 sm:p-4"
      >
        <Modal
          className="w-full max-w-full p-0 sm:p-2 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
          isDismissable={isDismissable}
        >
          <div
            ref={containerRef}
            className={twMerge("w-full", isTransitioning && "transition-transform duration-200")}
            style={{
              transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
            }}
          >
            <RACDialog
              className={twMerge(
                // Base structure
                "relative flex w-full flex-col overflow-hidden",
                // Background and borders with elevation
                "bg-background-surface",
                "shadow-2xl shadow-black/30 dark:shadow-black/60",
                "border-t border-x border-border-default/40",
                // Suppress browser focus outline/rings on the dialog container itself
                // This is safe: React Aria manages focus on interactive elements inside the dialog.
                // The container doesn't need a focus indicator as it's not directly interactive.
                "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 focus:shadow-none",
                // Transform origin for scale animation
                "origin-bottom",
                // Entrance animations using combined keyframes with enterprise-grade timing
                animationVariant === "scale"
                  ? "animate-[tray-enter-scale_400ms_cubic-bezier(0.32,0.72,0,1)]"
                  : "animate-[tray-enter_400ms_cubic-bezier(0.32,0.72,0,1)]",
                // Exit animations using data-[exiting] state - snappier for responsiveness
                animationVariant === "scale"
                  ? "data-[exiting]:animate-[tray-exit-scale_250ms_cubic-bezier(0.32,0,0.67,0)]"
                  : "data-[exiting]:animate-[tray-exit_250ms_cubic-bezier(0.32,0,0.67,0)]",
                className
              )}
              style={{
                maxWidth,
                // When the iOS soft keyboard is open, window.visualViewport.height
                // is smaller than the full window height. We use that value (minus a
                // small top offset so the tray doesn't touch the status bar) as the
                // ceiling, which keeps the tray — including its footer — fully visible
                // above the keyboard. Falls back to the prop value when visualViewport
                // is unavailable or hasn't been measured yet.
                ...(height
                  ? { height }
                  : {
                      // Use the smaller of:
                      // 1. The prop maxHeight (e.g. "80vh") — designer-controlled ceiling
                      // 2. The live visual viewport height (JS, tracks keyboard in real-time)
                      // 3. 100dvh — CSS-native dynamic viewport height, shrinks with iOS keyboard on Safari 15.4+
                      // The dvh fallback ensures we never exceed the visible area even before
                      // visualViewport fires its first event (e.g. on initial mount or slow paint).
                      // CSS min() only accepts <length-percentage>, not keywords (e.g. "none",
                      // "max-content"). If maxHeight is a bare keyword, pass it through;
                      // otherwise compose with the live viewport clamp.
                      maxHeight: /\d/.test(maxHeight)
                        ? viewportHeight !== null
                          ? `min(${maxHeight}, ${Math.max(viewportHeight - 8, 0)}px, 100dvh)`
                          : `min(${maxHeight}, 100dvh)`
                        : maxHeight,
                    }),
              }}
            >
              {showHandle && (
                <div
                  className="absolute top-0 left-0 right-0 h-8 z-10 flex justify-center items-start pt-2 touch-none cursor-grab active:cursor-grabbing"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerEnd}
                  onPointerCancel={handlePointerEnd}
                >
                  <div
                    className={twMerge(
                      "h-1 w-12 rounded-full bg-border-muted transition-all duration-300",
                      "hover:bg-border-default hover:w-16",
                      isHandleAnimating && "animate-[handle-wiggle_0.6s_ease-in-out]"
                    )}
                  />
                </div>
              )}
              {hasHeader &&
                (hasBackArrow ? (
                  <DialogHeader
                    title={title}
                    headerContent={headerContent}
                    titleAlign={titleAlign}
                    hideCloseIcon={hideCloseIcon || !onClose}
                    hasBackArrow={true}
                    onBack={
                      onBack ??
                      (() => {
                        /* no-op */
                      })
                    }
                    onClose={
                      onClose ||
                      (() => {
                        /* Default handler */
                      })
                    }
                  />
                ) : (
                  <DialogHeader
                    title={title}
                    headerContent={headerContent}
                    titleAlign={titleAlign}
                    hideCloseIcon={hideCloseIcon || !onClose}
                    onClose={
                      onClose ||
                      (() => {
                        /* Default handler */
                      })
                    }
                  />
                ))}
              <div
                ref={scrollContentRef}
                className={twMerge("flex-1 min-h-0 overflow-y-auto overscroll-contain", contentPadding ? "p-4" : "p-0")}
              >
                {children}
              </div>
              {hasFooter && (
                <DialogFooter
                  primaryAction={primaryAction}
                  secondaryAction={secondaryAction}
                  footerContent={footerContent}
                />
              )}
            </RACDialog>
          </div>
        </Modal>
      </ModalBackdrop>
    </ErrorBoundary>
  );
}

Tray.displayName = "Tray";
// Export types
